import { useT } from '@transifex/react';
import { useEffect, useState, useRef } from 'react';
import type { MutableRefObject } from 'react';

import * as models from 'pkg/api/models';
import type { SaveableImage } from 'pkg/hooks/image-editor';
import { useImageEditor } from 'pkg/hooks/image-editor';
import useComponentDidMount from 'pkg/hooks/useComponentDidMount';

import MaterialSymbol from 'components/material-symbols';
import { useStepModalContext } from 'components/step-modal';

import FileInput from 'components/form/File';
import { submitForm } from 'components/form/Form';
import { RangeSlider } from 'components/form/inputs';
import Column from 'components/layout/column';

import Button from 'design/button';

import * as css from './styles.css';

interface Props {
	formRef: MutableRefObject<HTMLFormElement>;
	defaultValue?: models.attachment.Attachment;
	originalFilename?: string;
}

export function ImageUpload({
	formRef,
	defaultValue,
	originalFilename,
}: Props) {
	const t = useT();
	const ref = useRef(null);

	const { withNext } = useStepModalContext();

	const [canvasSize, setCanvasSize] = useState<number>(320);
	const [scale, setScale] = useState<number>(1);

	const editor = useImageEditor({
		width: canvasSize,
		height: canvasSize,
		imageWidth: canvasSize * 3,
		gutterRoundness: 20,
		maskOpacity: 1,

		onSuccess: () => {
			setScale(1);
		},
		onSave: async (blob: SaveableImage) => {
			submitForm(formRef, {
				type: models.attachment.AttachmentType.Image,
				visibility: models.attachment.AttachmentVisibility.Hidden,
				originalFilename,
				file: blob as File,
			});
		},
	});

	useComponentDidMount(() => {
		setCanvasSize(ref.current.getBoundingClientRect().width);

		if (editor && defaultValue) {
			editor.load(defaultValue.url);
		}
	});

	useEffect(() => {
		withNext(async () => {
			// if no image is selected, submit the form as it is
			if (!editor.hasImage) {
				submitForm(formRef);
			}

			await editor.save();

			return false;
		});

		return () => {
			withNext(async () => true);
		};
	}, [withNext, editor.hasImage]);

	const handleImageSelect = async (files: File[]) => {
		await editor.load(files[0]);
	};

	const handleImageScaleChange = (scale: number) => {
		setScale(scale);
		editor.scale(scale);
	};

	return (
		<Column>
			<div
				className={css.canvasWrapper}
				style={{ height: `${canvasSize}px` }}
				ref={ref}>
				{editor.canvas}
				<FileInput
					accept="image/*"
					allowDrop
					className={editor.hasImage && css.fileTrigger}
					onChange={handleImageSelect}>
					{editor.hasImage ? (
						<MaterialSymbol variant="edit" />
					) : (
						<Button icon="image" secondary>
							{t('Upload image')}
						</Button>
					)}
				</FileInput>
			</div>

			{editor.hasImage && (
				<RangeSlider
					min={0.25}
					max={3}
					step={0.01}
					value={scale}
					withButtons
					onChange={handleImageScaleChange}
				/>
			)}
		</Column>
	);
}
